<div class="campaign-table-container">
  <!-- Loading State -->
  <ng-container *ngIf="loading; else notLoadingTemplate">
    <div class="table-scroll-wrapper" #tableScrollWrapper>
      <div class="table-skeleton">
        <div class="table-skeleton-header">
          <div class="th" style="min-width: 250px">Name</div>
          <div class="th" style="min-width: 140px">Status</div>
          <div class="th" style="min-width: 170px">Location</div>
          <div class="th" style="min-width: 170px">User Segment</div>
          <div class="th" style="min-width: 160px">Promo Code</div>
          <div class="th" style="min-width: 150px">Start Date</div>
          <div class="th" style="min-width: 190px">Platforms</div>
          <div class="th" style="min-width: 200px">Performance</div>
          <div class="th" style="min-width: 200px">Goals</div>
          <div class="th" style="min-width: 228px">Actions</div>
        </div>
        <div class="table-skeleton-body">
          <div class="tr" *ngFor="let _ of skeletonRows; trackBy: trackBySkeletonId">
            <div
              class="td"
              style="min-width: 250px; display: flex; flex-direction: column; gap: 6px"
            >
              <div class="shimmer-line w-180"></div>
              <div class="shimmer-line w-140"></div>
            </div>
            <div class="td" style="min-width: 140px"><div class="shimmer-pill w-80"></div></div>
            <div class="td" style="min-width: 170px"><div class="shimmer-line w-120"></div></div>
            <div class="td" style="min-width: 170px"><div class="shimmer-line w-100"></div></div>
            <div class="td" style="min-width: 160px"><div class="shimmer-line w-90"></div></div>
            <div class="td" style="min-width: 150px"><div class="shimmer-line w-80"></div></div>
            <div class="td" style="min-width: 190px; display: flex; gap: 8px">
              <div class="shimmer-pill w-60"></div>
              <div class="shimmer-pill w-80"></div>
            </div>
            <div
              class="td"
              style="min-width: 200px; display: flex; flex-direction: column; gap: 7px"
            >
              <div class="shimmer-line w-150"></div>
              <div class="shimmer-line w-120"></div>
              <div class="shimmer-line w-140"></div>
            </div>
            <div
              class="td"
              style="min-width: 200px; display: flex; flex-direction: column; gap: 7px"
            >
              <div class="shimmer-line w-160"></div>
              <div class="shimmer-line w-180"></div>
              <div class="shimmer-line w-140"></div>
            </div>
            <div class="td" style="min-width: 228px; display: flex; gap: 20px">
              <div class="shimmer-circle small"></div>
              <div class="shimmer-circle small"></div>
              <div class="shimmer-circle small"></div>
              <div class="shimmer-circle small"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </ng-container>

  <!-- Not Loading State -->
  <ng-template #notLoadingTemplate>
    <!-- Table with Data -->
    <ng-container *ngIf="campaigns.length > 0">
      <div class="table-scroll-wrapper" #tableScrollWrapper>
        <table
          mat-table
          [dataSource]="dataSource"
          matSort
          class="mat-mdc-table campaign-mat-table"
          data-testid="campaigns-table"
        >
          <!-- Campaign Name Column -->
          <ng-container matColumnDef="campaign_name">
            <th
              mat-header-cell
              *matHeaderCellDef
              mat-sort-header="campaign_name"
              style="min-width: 250px"
            >
              Name
            </th>
            <td mat-cell *matCellDef="let campaign">
              <div class="campaign-name-cell">
                <span
                  class="campaign-name p3 text-semibold clickable"
                  (click)="onViewCampaignDetails(campaign)"
                >
                  {{ campaign.name }}
                </span>
                <span class="created-date p7">
                  Created on {{ formatCreatedDate(campaign.created_at) }}
                </span>
              </div>
            </td>
          </ng-container>

          <!-- Campaign Status Column -->
          <ng-container matColumnDef="campaign_status">
            <th mat-header-cell *matHeaderCellDef style="min-width: 140px">Status</th>
            <td mat-cell *matCellDef="let campaign">
              <span
                [ngClass]="{
                  'status-active': campaign.campaign_status === 'active',
                  'status-scheduled': campaign.campaign_status === 'scheduled',
                  'status-stopped': campaign.campaign_status === 'stopped',
                  'status-completed': campaign.campaign_status === 'completed',
                  'status-draft': campaign.campaign_status === 'draft',
                  'status-archived': campaign.campaign_status === 'archived',
                  'status-paused': campaign.campaign_status === 'paused',
                  'status-resumed': campaign.campaign_status === 'resumed',
                }"
              >
                {{ getStatusDisplay(campaign.campaign_status) }}
              </span>
            </td>
          </ng-container>

          <!-- Location Column -->
          <ng-container matColumnDef="location">
            <th mat-header-cell *matHeaderCellDef style="min-width: 170px">Location</th>
            <td mat-cell *matCellDef="let campaign">
              <div
                class="location-cell p5 text-underline"
                [matTooltip]="getLocationTooltipText(campaign)"
                matTooltipClass="campaign-table-tooltip"
                matTooltipPosition="below"
              >
                {{
                  campaign.locations?.length > 0
                    ? campaign.locations.length + ' locations'
                    : 'No locations'
                }}
              </div>
            </td>
          </ng-container>

          <!-- User Segment Column -->
          <ng-container matColumnDef="user_segment">
            <th mat-header-cell *matHeaderCellDef style="min-width: 170px">User Segment</th>
            <td mat-cell *matCellDef="let campaign">
              <div
                class="user-segment-cell p5 text-underline"
                [matTooltip]="getSegmentsTooltipText(campaign)"
                matTooltipClass="campaign-table-tooltip"
                matTooltipPosition="below"
              >
                {{
                  campaign.segments?.length > 0
                    ? campaign.segments.length + ' segments'
                    : 'No segments'
                }}
              </div>
            </td>
          </ng-container>

          <!-- Promo Code Column -->
          <ng-container matColumnDef="promo_code">
            <th mat-header-cell *matHeaderCellDef style="min-width: 160px">Promo Code</th>
            <td mat-cell *matCellDef="let campaign">
              <div class="promo-code-cell p5">
                {{ campaign.promo_code?.trim() || '—' }}
              </div>
            </td>
          </ng-container>

          <!-- Start Date Column -->
          <ng-container matColumnDef="start_date">
            <th
              mat-header-cell
              *matHeaderCellDef
              mat-sort-header="start_date"
              style="min-width: 205px"
            >
              Start Date
            </th>
            <td mat-cell *matCellDef="let campaign">
              <div class="start-date-cell p5">
                {{ formatStartDate(campaign.schedule_start_date || campaign.start_date) }}
              </div>
            </td>
          </ng-container>

          <!-- Platforms Column -->
          <ng-container matColumnDef="platforms">
            <th mat-header-cell *matHeaderCellDef style="min-width: 190px">Platforms</th>
            <td mat-cell *matCellDef="let campaign">
              <div class="platforms-cell">
                <span
                  *ngFor="let platform of campaign.platforms; trackBy: trackByIndex"
                  class="platform-tag"
                >
                  {{ getCampaignTypeDisplay(getPlatformKey(platform)) }}
                </span>
              </div>
            </td>
          </ng-container>

          <!-- Performance Column -->
          <ng-container matColumnDef="performance">
            <th mat-header-cell *matHeaderCellDef style="min-width: 230px">Performance</th>
            <td mat-cell *matCellDef="let campaign">
              <app-campaign-performance-cell [campaign]="campaign" />
            </td>
          </ng-container>

          <!-- Goals Column -->
          <ng-container matColumnDef="goals">
            <th mat-header-cell *matHeaderCellDef style="min-width: 280px">Goals</th>
            <td mat-cell *matCellDef="let campaign">
              <div class="goals-cell">
                <div
                  class="goal-item"
                  *ngFor="let goal of campaign.goals; trackBy: trackByGoalName"
                >
                  <span class="p5">{{ formatGoalDisplay(goal) }}</span>
                </div>
                <div class="goal-item" *ngIf="!campaign.goals || campaign.goals.length === 0">
                  <span class="p5">No goals set</span>
                </div>
              </div>
            </td>
          </ng-container>

          <!-- Actions Column -->
          <ng-container matColumnDef="actions">
            <th mat-header-cell *matHeaderCellDef class="actions-header">Actions</th>
            <td mat-cell *matCellDef="let campaign" class="actions-cell">
              <div class="action-buttons">
                <button
                  type="button"
                  class="action-btn pause-btn"
                  [attr.data-testid]="'campaign-pause-button-' + campaign.id"
                  [disabled]="
                    campaign.campaign_status === 'stopped' ||
                    campaign.campaign_status === 'archived' ||
                    campaign.campaign_status === 'completed' ||
                    campaign.campaign_status === 'draft'
                  "
                  (click)="onPauseCampaign(campaign)"
                >
                  <svg
                    *ngIf="campaign.campaign_status !== 'paused'"
                    width="10"
                    height="11"
                    viewBox="0 0 10 11"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M1 1.50004C1 1.32323 1.07024 1.15366 1.19526 1.02864C1.32029 0.903612 1.48986 0.833374 1.66667 0.833374H3C3.17681 0.833374 3.34638 0.903612 3.4714 1.02864C3.59643 1.15366 3.66667 1.32323 3.66667 1.50004V9.50004C3.66667 9.67685 3.59643 9.84642 3.4714 9.97145C3.34638 10.0965 3.17681 10.1667 3 10.1667H1.66667C1.48986 10.1667 1.32029 10.0965 1.19526 9.97145C1.07024 9.84642 1 9.67685 1 9.50004V1.50004Z"
                      stroke="#15172E"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M6.33333 1.50004C6.33333 1.32323 6.40357 1.15366 6.5286 1.02864C6.65362 0.903612 6.82319 0.833374 7 0.833374H8.33333C8.51014 0.833374 8.67971 0.903612 8.80474 1.02864C8.92976 1.15366 9 1.32323 9 1.50004V9.50004C9 9.67685 8.92976 9.84642 8.80474 9.97145C8.67971 10.0965 8.51014 10.1667 8.33333 10.1667H7C6.82319 10.1667 6.65362 10.0965 6.5286 9.97145C6.40357 9.84642 6.33333 9.67685 6.33333 9.50004V1.50004Z"
                      stroke="#15172E"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  <svg
                    *ngIf="campaign.campaign_status === 'paused'"
                    width="12"
                    height="13"
                    viewBox="0 0 12 13"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M1.66797 1.16675V11.8334L10.3346 6.50008L1.66797 1.16675Z"
                      fill="#F1F2F9"
                      stroke="#15172E"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </button>
                <button
                  type="button"
                  class="action-btn stop-btn"
                  [attr.data-testid]="'campaign-stop-button-' + campaign.id"
                  [disabled]="
                    campaign.campaign_status === 'stopped' ||
                    campaign.campaign_status === 'archived' ||
                    campaign.campaign_status === 'completed' ||
                    campaign.campaign_status === 'draft'
                  "
                  (click)="onStopCampaign(campaign)"
                >
                  <svg
                    width="12"
                    height="11"
                    viewBox="0 0 12 11"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M1.33203 2.16671C1.33203 1.81309 1.47251 1.47395 1.72256 1.2239C1.9726 0.97385 2.31174 0.833374 2.66536 0.833374H9.33203C9.68565 0.833374 10.0248 0.97385 10.2748 1.2239C10.5249 1.47395 10.6654 1.81309 10.6654 2.16671V8.83337C10.6654 9.187 10.5249 9.52613 10.2748 9.77618C10.0248 10.0262 9.68565 10.1667 9.33203 10.1667H2.66536C2.31174 10.1667 1.9726 10.0262 1.72256 9.77618C1.47251 9.52613 1.33203 9.187 1.33203 8.83337V2.16671Z"
                      stroke="#15172E"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </button>
                <button
                  type="button"
                  class="action-btn mark-complete-btn"
                  [attr.data-testid]="'campaign-mark-complete-button-' + campaign.id"
                  [disabled]="
                    campaign.campaign_status === 'draft' || campaign.campaign_status === 'scheduled'
                  "
                  (click)="onMarkComplete(campaign)"
                >
                  <svg
                    width="10"
                    height="11"
                    viewBox="0 0 10 11"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M1 9.5V7.5M3.66667 9.5V5.5M6.33333 9.5V3.5M9 9.5V1.5"
                      stroke="#15172E"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </button>
                <button
                  type="button"
                  class="action-btn menu-btn"
                  [attr.data-testid]="'campaign-actions-menu-button-' + campaign.id"
                  [matMenuTriggerFor]="campaignActionMenu"
                  (click)="showActionMenu($event, campaign)"
                  aria-label="Campaign actions"
                >
                  <span class="menu-icon" aria-hidden="true"></span>
                </button>
              </div>
            </td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
          <tr
            mat-row
            *matRowDef="let row; columns: displayedColumns"
            [attr.data-testid]="'campaigns-table-row-' + row.id"
          ></tr>
        </table>

        <mat-menu #campaignActionMenu="matMenu" class="campaign-action-menu">
          <button mat-menu-item data-testid="campaign-view-menu-item" (click)="onViewCampaign()">
            <span>View</span>
          </button>
          <button
            mat-menu-item
            *ngIf="currentCampaign?.campaign_status !== 'archived'"
            data-testid="campaign-duplicate-menu-item"
            (click)="onDuplicateCampaign()"
          >
            <span>Duplicate</span>
          </button>
          <button mat-menu-item data-testid="campaign-edit-menu-item" (click)="onEditCampaign()">
            <span>Edit</span>
          </button>
          <button
            mat-menu-item
            class="archive-item"
            *ngIf="currentCampaign?.campaign_status !== 'archived'"
            data-testid="campaign-archive-menu-item"
            (click)="onArchiveCampaign()"
          >
            <span>Archive</span>
          </button>
        </mat-menu>
      </div>

      <!-- Pagination -->
      <div class="pagination-container">
        <!-- Left: Navigation Controls -->
        <div class="pagination-navigation"></div>

        <!-- Center: Items per page dropdown -->
        <div class="pagination-center">
          <div class="items-per-page">
            <span class="items-per-page-label">Items per page</span>
            <mat-select
              [(ngModel)]="rowsPerPage"
              [style]="{ width: '80px' }"
              (selectionChange)="onRowsPerPageChange($event)"
            >
              <mat-option
                *ngFor="let rowsPerPage of rowsPerPageOptions"
                [value]="rowsPerPage.value"
                >{{ rowsPerPage.label }}</mat-option
              >
            </mat-select>
          </div>
        </div>

        <!-- Right: Current page report -->
        <div class="pagination-info">
          <span class="current-page-report">
            Items {{ currentPage * rowsPerPage + 1 }} - {{ getEndItem() }} of {{ totalRecords }}
          </span>
        </div>
      </div>
    </ng-container>

    <!-- Empty State -->
    <app-empty-state
      testId="campaigns-no-results-empty-state"
      *ngIf="campaigns.length === 0 && originalCampaignCount !== 0"
      class="campaign-empty-state"
      image="assets/images/package/Illustrations.svg"
      imageAlt="No Campaigns"
      [title]="emptyStateConfig.title"
      [description]="emptyStateConfig.description"
      actionLabel="Create Campaign"
      [showAction]="emptyStateConfig.showAddButton"
      (action)="onAddCampaign()"
    >
    </app-empty-state>
  </ng-template>
</div>
<!-- Type Filter Panel -->
<p-popover
  #typeFilterPanel
  [style]="{ width: '250px' }"
  styleClass="type-filter-panel"
  (onHide)="isTypeFilterOpen = false"
>
  <ng-template pTemplate>
    <div class="type-filter-content">
      <div class="filter-options">
        <div *ngFor="let option of typeOptions; trackBy: trackByValue" class="p-field-checkbox">
          <mat-checkbox [value]="option.value" [(ngModel)]="selectedTypes"></mat-checkbox>
          <label [for]="option.value" class="p5">{{ option.label }}</label>
        </div>
      </div>
      <div class="filter-actions">
        <button
          type="button"
          label="Clear"
          style="padding-left: 0px"
          class="btn btn-tertiary"
          [disabled]="!hasTypeFilterValues"
          (click)="clearTypeFilter()"
          [ngClass]="{ disabled: !hasTypeFilterValues }"
        >
          Clear
        </button>
        <div class="right-buttons">
          <button
            type="button"
            label="Cancel"
            class="btn btn-secondary btn-small"
            (click)="typeFilterPanel.hide()"
          >
            Cancel
          </button>
          <button
            type="button"
            label="Apply"
            class="btn btn-primary btn-small"
            (click)="applyTypeFilter()"
            [disabled]="!selectedTypes || selectedTypes.length === 0"
            [ngClass]="{ disabled: !selectedTypes || selectedTypes.length === 0 }"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  </ng-template>
</p-popover>

<!-- Status Filter Panel -->
<p-popover
  #statusFilterPanel
  [style]="{ width: '250px' }"
  styleClass="status-filter-panel"
  (onHide)="isStatusFilterOpen = false"
>
  <ng-template pTemplate>
    <div class="status-filter-content">
      <div class="filter-options">
        <div *ngFor="let option of statusOptions; trackBy: trackByValue" class="p-field-checkbox">
          <mat-checkbox [value]="option.value" [(ngModel)]="selectedStatuses"></mat-checkbox>
          <label [for]="option.value" class="p5">{{ option.label }}</label>
        </div>
      </div>
      <div class="filter-actions">
        <button
          type="button"
          label="Clear"
          style="padding-left: 0px"
          class="btn btn-tertiary btn-small"
          [disabled]="!hasStatusFilterValues"
          (click)="clearStatusFilter()"
          [ngClass]="{ disabled: !hasStatusFilterValues }"
        >
          Clear
        </button>
        <div class="right-buttons">
          <button
            type="button"
            label="Cancel"
            class="btn btn-secondary btn-small"
            (click)="statusFilterPanel.hide()"
          >
            Cancel
          </button>
          <button
            type="button"
            label="Apply"
            class="btn btn-primary btn-small"
            (click)="applyStatusFilter()"
            [disabled]="!selectedStatuses || selectedStatuses.length === 0"
            [ngClass]="{ disabled: !selectedStatuses || selectedStatuses.length === 0 }"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  </ng-template>
</p-popover>

<!-- Location Filter Panel -->
<p-popover
  #locationFilterPanel
  [style]="{ width: '250px' }"
  styleClass="location-filter-panel"
  (onHide)="isLocationFilterOpen = false"
>
  <ng-template pTemplate>
    <div class="location-filter-content">
      <div class="filter-options">
        <div *ngFor="let option of locationOptions; trackBy: trackByValue" class="p-field-checkbox">
          <mat-checkbox [value]="option.value" [(ngModel)]="selectedLocations"></mat-checkbox>
          <label [for]="option.value" class="p5">{{ option.label }}</label>
        </div>
      </div>
      <div class="filter-actions">
        <button
          type="button"
          label="Clear"
          style="padding-left: 0px"
          class="btn btn-tertiary"
          [disabled]="!hasLocationFilterValues"
          (click)="clearLocationFilter()"
          [ngClass]="{ disabled: !hasLocationFilterValues }"
        >
          Clear
        </button>
        <div class="right-buttons">
          <button
            type="button"
            label="Cancel"
            class="btn btn-secondary btn-small"
            (click)="locationFilterPanel.hide()"
          >
            Cancel
          </button>
          <button
            type="button"
            label="Apply"
            class="btn btn-primary btn-small"
            (click)="applyLocationFilter()"
            [disabled]="!selectedLocations || selectedLocations.length === 0"
            [ngClass]="{ disabled: !selectedLocations || selectedLocations.length === 0 }"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  </ng-template>
</p-popover>

<!-- Duplicate Campaign Modal -->
<app-duplicate-campaign-modal
  *ngIf="showDuplicateModal"
  [campaign]="currentCampaign"
  (confirmDuplicate)="onConfirmDuplicate($event)"
  (close)="onCloseDuplicateModal()"
>
</app-duplicate-campaign-modal>

<!-- Archive Campaign Modal -->
<app-archive-campaign-modal
  *ngIf="showArchiveModal"
  [campaign]="currentCampaign"
  (confirmArchive)="onConfirmArchive($event)"
  (close)="onCloseArchiveModal()"
>
</app-archive-campaign-modal>

<!-- Pause Campaign Modal -->
<app-pause-campaign-modal
  *ngIf="showPauseModal"
  [campaign]="currentCampaign"
  (confirmPause)="onConfirmPause($event)"
  (close)="onClosePauseModal()"
>
</app-pause-campaign-modal>

<!-- Stop Campaign Modal -->
<app-stop-campaign-modal
  *ngIf="showStopModal"
  [campaign]="currentCampaign"
  (confirmStop)="onConfirmStop($event)"
  (close)="onCloseStopModal()"
>
</app-stop-campaign-modal>

<!-- View Campaign Modal -->
<app-view-campaign-modal
  *ngIf="showViewModal"
  [campaignId]="currentCampaign?.id"
  [visible]="showViewModal"
  (close)="onCloseViewModal()"
  (edit)="onEditCampaign()"
  (campaignDuplicated)="onCampaignDuplicated()"
  (campaignArchived)="onCampaignArchived()"
  (campaignPaused)="onCampaignPaused()"
  (campaignStopped)="onCampaignStopped()"
  (campaignMarkedComplete)="onCampaignMarkedComplete()"
>
</app-view-campaign-modal>

<!-- Empty State -->
<app-empty-state
  testId="campaigns-empty-state"
  *ngIf="originalCampaignCount === 0 && !loading && hasLoadedInitialCampaigns"
  class="campaign-empty-state"
  image="assets/images/package/Illustrations.svg"
  imageAlt="No Campaigns"
  [title]="emptyStateConfig.title"
  [description]="emptyStateConfig.description"
  actionLabel="Create Campaign"
  [showAction]="emptyStateConfig.showAddButton"
  (action)="onAddCampaign()"
>
</app-empty-state>
